import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

/**
 * Unified storage utility that works on both web and mobile
 * Uses Capacitor Preferences for mobile and localStorage for web
 */

const isNative = Capacitor.isNativePlatform();

export const storage = {
  /**
   * Save a value to storage
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be JSON stringified)
   */
  async set(key, value) {
    const stringValue = JSON.stringify(value);
    
    if (isNative) {
      await Preferences.set({ key, value: stringValue });
    } else {
      localStorage.setItem(key, stringValue);
    }
  },

  /**
   * Get a value from storage
   * @param {string} key - Storage key
   * @returns {Promise<any>} - Parsed value or null if not found
   */
  async get(key) {
    if (isNative) {
      const { value } = await Preferences.get({ key });
      return value ? JSON.parse(value) : null;
    } else {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    }
  },

  /**
   * Remove a value from storage
   * @param {string} key - Storage key
   */
  async remove(key) {
    if (isNative) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  },

  /**
   * Clear all storage
   */
  async clear() {
    if (isNative) {
      await Preferences.clear();
    } else {
      localStorage.clear();
    }
  },

  /**
   * Get all keys from storage
   * @returns {Promise<string[]>} - Array of keys
   */
  async keys() {
    if (isNative) {
      const { keys } = await Preferences.keys();
      return keys;
    } else {
      return Object.keys(localStorage);
    }
  }
};

// Auth-specific storage helpers
export const authStorage = {
  /**
   * Save authentication data
   * @param {object} authData - { token, user }
   */
  async saveAuth(authData, remember = true) {
    await storage.set('auth_token', authData.token);
    await storage.set('auth_user', authData.user);
    await storage.set('auth_remember', remember);

    // Mark this browser/app session as active so "remember me off" users
    // stay logged in across reloads but not after the app is fully closed.
    try { window.sessionStorage.setItem('yachangu_session_active', '1'); } catch (e) { /* no-op */ }

    // Backward compatibility: also save user to 'userData' key
    // Some components still use localStorage.getItem('userData')
    if (!isNative) {
      localStorage.setItem('userData', JSON.stringify(authData.user));
    }
    
    console.log('✅ Auth data saved to storage');
  },

  /**
   * Get authentication data
   * @returns {Promise<{token: string, user: object} | null>}
   */
  async getAuth() {
    const token = await storage.get('auth_token');
    const user = await storage.get('auth_user');
    
    if (token && user) {
      console.log('✅ Auth data loaded from storage');
      return { token, user };
    }
    
    return null;
  },

  /**
   * Clear authentication data
   */
  async clearAuth() {
    await storage.remove('auth_token');
    await storage.remove('auth_user');
    await storage.remove('auth_remember');
    try { window.sessionStorage.removeItem('yachangu_session_active'); } catch (e) { /* no-op */ }
    
    // Backward compatibility: also clear userData
    if (!isNative) {
      localStorage.removeItem('userData');
    }
    
    console.log('✅ Auth data cleared from storage');
  },

  /**
   * Check if user is authenticated
   * @returns {Promise<boolean>}
   */
  async isAuthenticated() {
    const token = await storage.get('auth_token');
    return !!token;
  }
};

export default storage;
